import {
  promptOrderProblems,
  promptVariables,
  SHARED_PROMPT_ROOTS,
  sharedPromptProblems,
  type IPromptMessage,
} from '@hookpipe/contracts/flows/services/flow-prompt';

import { fieldsOf, valueType, type IDataSource, type IReaderData } from '../flow-data';
import type { TJsonValue, TLibraryKind, TLibraryMessage } from './api';

export const KIND_LABEL: Record<TLibraryKind, string> = { code: 'Code', prompt: 'Prompt' };

export const ROLE_OPTIONS = [
  { value: 'system' as const, label: 'System' },
  { value: 'user' as const, label: 'User' },
  { value: 'assistant' as const, label: 'Assistant' },
];

export const EMPTY_PROMPT: TLibraryMessage[] = [{ role: 'user', content: '' }];

export const EMPTY_CODE = `export default (event, ctx) => {
  return { ...event, handled: true };
};
`;

export const STARTER_PROMPT: TLibraryMessage[] = [
  { role: 'system', content: 'You answer in one short paragraph, in plain words.' },
  { role: 'user', content: 'Summarise this event for the team:\n\n{{event}}' },
];

export const variablesOf = (messages: readonly TLibraryMessage[]): string[] =>
  promptVariables(messages as IPromptMessage[]);

const keyed = (problems: readonly { path: (string | number)[]; message: string }[]) => {
  const found = new Map<string, string>();
  for (const problem of problems) {
    const key = ['messages', ...problem.path.map(String)].join('.');
    if (!found.has(key)) found.set(key, problem.message);
  }
  return found;
};

/** Problems keyed `messages.2.role`, as the API names them, for a list being typed. */
export const orderProblems = (messages: readonly TLibraryMessage[]): Map<string, string> =>
  keyed(promptOrderProblems(messages));

/** A Library prompt's own rules on top: only `event`, `trigger` and `vars` may be read (§16). */
export const sharedProblems = (messages: readonly TLibraryMessage[]): Map<string, string> =>
  keyed(sharedPromptProblems(messages as IPromptMessage[]));

export interface IPromptSample {
  event: unknown;
  trigger: unknown;
  vars: Record<string, string>;
}

// What a Library prompt's template picker offers: the sample event and trigger, and one field
// per declared variable. Only these roots parse, so `{{nodes.x}}` shows as a broken chip.
export const promptReaderData = (
  sample: IPromptSample,
  variables: readonly string[],
): IReaderData => {
  const sources: IDataSource[] = [];
  if (sample.event !== undefined && sample.event !== null) {
    sources.push({
      key: 'event',
      name: 'Event',
      type: 'event',
      hasSample: true,
      fields: fieldsOf(sample.event, 'event'),
    });
  }
  if (sample.trigger !== undefined && sample.trigger !== null) {
    sources.push({
      key: 'trigger',
      name: 'Trigger',
      type: 'trigger',
      hasSample: true,
      fields: fieldsOf(sample.trigger, 'trigger'),
    });
  }
  sources.push({
    key: 'vars',
    name: 'Variables',
    type: 'vars',
    hasSample: variables.length > 0,
    fields: variables.map((name) => {
      const value = sample.vars[name] ?? '';
      return { path: `vars.${name}`, label: name, type: valueType(value) ?? 'text', value };
    }),
  });
  return {
    sources,
    unreadable: [],
    context: null,
    names: new Map(),
    explain: () => null,
    roots: SHARED_PROMPT_ROOTS,
  };
};

export const parseSample = (text: string): { value: TJsonValue; error: string | null } => {
  if (text.trim() === '') return { value: null, error: null };
  try {
    const value: TJsonValue = JSON.parse(text);
    return { value, error: null };
  } catch (error) {
    return { value: null, error: error instanceof Error ? error.message : 'not JSON' };
  }
};
